import { Sender } from '@ant-design/x'
import { Tooltip } from 'antd'
import type { GetRef } from 'antd'
import { Send } from 'lucide-react'
import { forwardRef, useImperativeHandle, useRef } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { useStyles } from './styles'

const MAX_ROWS = 8

export type ChatComposerHandle = {
  focus: () => void
}

type Props = {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  onStop: () => void
  streaming: boolean
  disabled?: boolean
  placeholder?: string
  header?: ReactNode
  prefix?: ReactNode
  className?: string
  minRows?: number
  onPasteFiles?: (files: File[]) => void
  /** A message may consist of files only. */
  allowEmptySend?: boolean
  /** Holds the send button, e.g. while files are still uploading. */
  sendDisabled?: boolean
}

/** What a surface adds to a composer it does not render itself. */
export type ChatComposerExtras = Pick<
  Props,
  'header' | 'prefix' | 'onPasteFiles' | 'allowEmptySend' | 'sendDisabled'
>

export const ChatComposer = forwardRef<ChatComposerHandle, Props>(
  function ChatComposer(
    {
      value,
      onChange,
      onSend,
      onStop,
      streaming,
      disabled,
      placeholder,
      header,
      prefix,
      className,
      minRows = 1,
      onPasteFiles,
      allowEmptySend,
      sendDisabled
    },
    ref
  ) {
    const { styles, cx } = useStyles()
    const { t } = useTranslation()
    const innerRef = useRef<GetRef<typeof Sender>>(null)

    useImperativeHandle(ref, () => ({
      focus: () => innerRef.current?.focus()
    }))

    return (
      <Sender
        ref={innerRef}
        className={cx(styles.composer, className)}
        value={value}
        classNames={{ content: styles.composerContent }}
        onChange={(next) => onChange(next)}
        onSubmit={() => onSend()}
        onCancel={onStop}
        onPasteFile={
          onPasteFiles ? (files) => onPasteFiles(Array.from(files)) : undefined
        }
        loading={streaming}
        disabled={disabled}
        placeholder={placeholder ?? t('workspaceHome.chatPlaceholder')}
        header={header}
        prefix={prefix}
        autoSize={{ minRows, maxRows: MAX_ROWS }}
        suffix={(_, { components }) => {
          const { SendButton, LoadingButton } = components

          return streaming ? (
            <Tooltip title={t('workspaceHome.composer.stop')}>
              <LoadingButton aria-label={t('workspaceHome.composer.stop')} />
            </Tooltip>
          ) : (
            <Tooltip title={t('workspaceHome.composer.send')}>
              <SendButton
                icon={<Send size={16} />}
                disabled={
                  disabled || sendDisabled || (!value.trim() && !allowEmptySend)
                }
                aria-label={t('workspaceHome.composer.send')}
              />
            </Tooltip>
          )
        }}
      />
    )
  }
)
